import { Suspense, useEffect } from 'react';

import { useSuspenseQueries } from '@tanstack/react-query';
import {
	createFileRoute,
	Link,
	Outlet,
	redirect,
	useChildMatches,
} from '@tanstack/react-router';

import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';

import {
	Box,
	Container,
	LinearProgress,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';

import { claimQueries } from '@repo/api/claim/claims';
import { countryQueries } from '@repo/api/countries';
import { ErrorBox } from '@repo/ui/ErrorBox';

import { Route as ClaimDataRoute } from '~pages/_claims/claim.$claimId/_claim/data';
import { Route as AverageRoute } from './calculations/average';
import { Route as CollectibleRoute } from './calculations/collectible';
import { Route as ExpensesRoute } from './calculations/expenses';
import { Route as BILossRoute } from './calculations/loss';
import { Route as DocumentsRoute } from './documents';

export const Route = createFileRoute(
	'/_claims/claim/$claimId/_claim/calculations',
)({
	beforeLoad: ({ params, context }) => {
		if (!context.user?.permissions.includes('claim.calculation.view')) {
			throw redirect({
				to: ClaimDataRoute.to,
				params,
			});
		}
	},
	notFoundComponent: () => <Typography>Not found</Typography>,
	component: function CalculationsPage() {
		const navigate = Route.useNavigate();

		const matchedId = useChildMatches({
			select: (match) => match[0]?.routeId,
		});

		useEffect(() => {
			if (!matchedId) {
				void navigate({
					to: BILossRoute.to,
					replace: true,
				});
			}
		}, [matchedId, navigate]);

		const { claimId } = Route.useParams();
		const [{ data: claim }, { data: countries }] = useSuspenseQueries({
			queries: [claimQueries.id(claimId), countryQueries.list()],
		});

		useEffect(() => {
			if (
				claim.claimDocuments.length === 0 ||
				!claim.claimDocuments.every(
					(document) => document.documentStatus === 'Completed',
				)
			) {
				void navigate({
					to: DocumentsRoute.to,
				});
			}
		}, [claim.claimDocuments, navigate]);

		const selectedCountryId = claim.claimInsured.countryId;

		const selectedCountryName =
			selectedCountryId ?
				countries.find((c) => c.id === selectedCountryId)?.name
			:	countries[0]?.name;

		const isUK = selectedCountryName === 'United Kingdom';

		const hasEPOI = !!claim.estimatedPeriodofIndemnity;

		if (!hasEPOI) {
			return (
				<Container
					sx={{ flexGrow: 1, mb: 2, display: 'flex', flexDirection: 'column' }}
				>
					<ErrorBox title="Calculations Unavailable" icon={faCircleExclamation}>
						<Typography variant="body2" sx={{ padding: 1 }}>
							Enter a Period of Indemnity in Claim Details to continue with
							calculations
						</Typography>
					</ErrorBox>
				</Container>
			);
		}

		const hasCoinsurance = !isUK && claim.claimPolicy?.coInsurance;
		const hasAverage = isUK && claim.claimPolicy?.hasAverageClausePerPolicy;

		return (
			<Container
				sx={{ flexGrow: 1, mb: 2, display: 'flex', flexDirection: 'column' }}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						flexGrow: 1,
						backgroundColor: 'offwhite.light',
						p: 1,
					}}
				>
					<Tabs
						value={matchedId}
						variant="scrollable"
						sx={{
							minHeight: '32px',
							'& .MuiTabs-flexContainer': {
								gap: 0.5,
							},
							'& .MuiTab-root': {
								backgroundColor: '#dddddd',
								color: '#424242',
								paddingBlock: 1,
								fontSize: '0.9rem',
								fontWeight: '500',
								textWrap: 'balance',
								flex: 1,
							},
							'& .Mui-selected': {
								color: '#424242',
								backgroundColor: 'white',
							},
						}}
						slotProps={{
							indicator: {
								sx: {
									transform: 'scaleX(0.7)',
									backgroundColor: 'accent.main',
									height: '3px',
								},
							},
						}}
					>
						<Tab
							value={BILossRoute.id}
							component={Link}
							to={BILossRoute.to}
							replace
							label="Bi Loss Summary"
						/>
						<Tab
							value={ExpensesRoute.id}
							component={Link}
							to={ExpensesRoute.to}
							replace
							label="Detailed BI Loss with Expense Savings Analysis"
						/>
						{hasCoinsurance && (
							<Tab
								value={CollectibleRoute.id}
								component={Link}
								to={CollectibleRoute.to}
								replace
								label="Collectible Rate"
							/>
						)}
						{hasAverage && (
							<Tab
								value={AverageRoute.id}
								component={Link}
								to={AverageRoute.to}
								replace
								label="Average"
							/>
						)}
					</Tabs>
					<Box
						sx={{
							flexGrow: 1,
							p: 2,
							backgroundColor: 'white',
						}}
					>
						<Suspense fallback={<LinearProgress />}>
							<Outlet />
						</Suspense>
					</Box>
				</Box>
			</Container>
		);
	},
	head: (ctx) => ctx.match.context.setTitle('Claim', 'Calculations'),
});
